import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSessionData } from './hooks/useSessionData';
import { useMediaPlayer } from './hooks/useMediaPlayer';
import { useMapIntegration } from './hooks/useMapIntegration';
import SessionsList from './components/SessionsList';
import PlayheadController from './components/PlayheadController';
import SessionSummary from './components/SessionSummary';
import MediaPlayer from './components/MediaPlayer';
import SignalPlotter from './components/SignalPlotter';
import LogViewer from './components/LogViewer';
import './MovableFrame.scss';

export default function SessionOverview({ isOpen, onClose, unitId, unitName: propUnitName }) {
  const [position, setPosition] = useState({ 
    x: window.innerWidth * 0.05, 
    y: window.innerHeight * 0.05 
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedProject, setSelectedProject] = useState(null);
  const [unitName, setUnitName] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [gpsMessages, setGpsMessages] = useState([]);
  const [filteredData, setFilteredData] = useState(null);
  const currentVideoTimeRef = useRef(0);
  const [loadingDbcInfo, setLoadingDbcInfo] = useState(false);
  const [canIds, setCanIds] = useState([]);
  const [signalsByCanId, setSignalsByCanId] = useState({});
  const [dbcFiles, setDbcFiles] = useState([]);
  const [selectedDbcFile, setSelectedDbcFile] = useState(null);

  // Custom hooks
  const {
    sessions,
    projects,
    projectInfo,
    sessionInfo,
    isLoadingSessions,
    canData,
    logData,
    dbLogData,
    loading,
    loadingChunks,
    resetSessionData,
    fetchLogChunk,
    loadedChunksRef
  } = useSessionData(unitId, selectedProject, selectedSession, isOpen);

  const {
    currentVideoTime,
    setCurrentVideoTime,
    sessionDuration,
    setSessionDuration,
    isPlaying,
    setIsPlaying,
    videoFiles,
    setVideoFiles,
    visibleFiles,
    setVisibleFiles,
    videoRefs,
    audioRef,
    resetMediaElements,
    handlePlayheadChange,
    handlePlayPause
  } = useMediaPlayer();

  const {
    currentGpsMessage,
    mapRef,
    mapContainerRef,
    clearRoute,
    recreateRoute
  } = useMapIntegration(gpsMessages, currentVideoTime);

  // Wrap handlePlayheadChange to fetch chunks immediately on manual seek
  const handlePlayheadChangeWithFetch = useCallback((newTime) => {
    handlePlayheadChange(newTime);
    fetchLogChunk(newTime);
  }, [handlePlayheadChange, fetchLogChunk]);

  // Dragging functionality
  const handleMouseDown = (e) => {
    if (e.target.closest('.frame-content')) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, handleMouseMove]);

  // Reset all state when closing
  useEffect(() => {
    if (!isOpen) {
      setSelectedProject(null);
      setSelectedSession(null);
      setGpsMessages([]);
      setFilteredData(null);
      setVisibleFiles([]);
      setVideoFiles([]);
      resetSessionData();
      resetMediaElements();
      clearRoute();
    }
  }, [isOpen]);

  // Set unit name from props
  useEffect(() => {
    if (propUnitName) {
      setUnitName(propUnitName);
    }
  }, [propUnitName]);

  // Set selected project from session data
  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0].folder);
    }
  }, [projects, selectedProject]);

  // Clear selected session when project changes
  useEffect(() => {
    if (selectedProject) {
      setSelectedSession(null);
    }
  }, [selectedProject]);

  // Reset all data when project changes (after session is cleared)
  useEffect(() => {
    if (selectedProject && !selectedSession) {
      resetSessionData();
      resetMediaElements();
      clearRoute();
      setGpsMessages([]);
      setFilteredData(null);
    }
  }, [selectedProject, selectedSession]);

  // Reset media elements when session changes
  useEffect(() => {
    if (selectedSession) {
      resetMediaElements();
    }
  }, [selectedSession]);

  // Extract GPS messages and video files from log data or session info
  useEffect(() => {
    if (logData && logData.gpsMessages && logData.gpsMessages.length > 0) {
      setGpsMessages(logData.gpsMessages);
    }
    
    // Get video files from sessionInfo (from hook) or projectInfo
    if (sessionInfo?.logFiles) {
      const videos = sessionInfo.logFiles.filter(f => ['mkv', 'avi'].includes(f.suffix));
      setVideoFiles(videos);
      const allFiles = sessionInfo.logFiles.map(f => f.filename);
      setVisibleFiles(allFiles);
    } else if (projectInfo?.sessions) {
      const projSessionInfo = projectInfo.sessions.find(s => s.objectId === selectedSession);
      if (projSessionInfo?.logFiles) {
        const videos = projSessionInfo.logFiles.filter(f => ['mkv', 'avi'].includes(f.suffix));
        setVideoFiles(videos);
        const allFiles = projSessionInfo.logFiles.map(f => f.filename);
        setVisibleFiles(allFiles);
      }
    }
    if (logData?.logs) {

      console.log("now:", selectedSession, logData.logs[0]?.timestamp)
    }
  }, [logData, sessionInfo, projectInfo, selectedSession]);

  // Get session duration from session info or video
  useEffect(() => {
    // First try to get duration from sessionInfo (from hook)
    if (sessionInfo?.endTimeMicros !== undefined && sessionInfo?.startTimeMicros !== undefined) {
      const durationSeconds = (sessionInfo.endTimeMicros - sessionInfo.startTimeMicros) / 1000000;
      setSessionDuration(durationSeconds);
    } else if (projectInfo?.sessions) {
      // Fallback to projectInfo
      const projSessionInfo = projectInfo.sessions.find(s => s.objectId === selectedSession);
      if (projSessionInfo?.endTimeMicros !== undefined && projSessionInfo?.startTimeMicros !== undefined) {
        const durationSeconds = (projSessionInfo.endTimeMicros - projSessionInfo.startTimeMicros) / 1000000;
        setSessionDuration(durationSeconds);
      }
    } else if (videoRefs.current[0]?.duration && !isNaN(videoRefs.current[0].duration)) {
      setSessionDuration(videoRefs.current[0].duration);
    }
  }, [sessionInfo, projectInfo, selectedSession, videoRefs.current[0]?.duration]);

  // Track current video time in ref
  useEffect(() => {
    currentVideoTimeRef.current = currentVideoTime;
  }, [currentVideoTime]);
  
  // Sync videos to master timer
  useEffect(() => {
    videoRefs.current.forEach(videoRef => {
      if (videoRef && Math.abs(videoRef.currentTime - currentVideoTime) > 0.5) {
        videoRef.currentTime = currentVideoTime;
      }
    });
    if (audioRef.current && Math.abs(audioRef.current.currentTime - currentVideoTime) > 0.5) {
      audioRef.current.currentTime = currentVideoTime;
    }
  }, [currentVideoTime]);
  
  // Fetch log chunks as video plays (every 5 seconds)
  useEffect(() => {
    if (!selectedSession) return;
    
    fetchLogChunk(currentVideoTimeRef.current);
    
    const intervalId = setInterval(() => {
      fetchLogChunk(currentVideoTimeRef.current);
    }, 5000);
    
    return () => clearInterval(intervalId);
  }, [selectedSession, fetchLogChunk]);

  // Sync with master playhead
  useEffect(() => {
    if (!canData) return;

    const currentTimeMicros = (canData.startTime || 0) + (currentVideoTime * 1000000);
    const relevantTimes = canData.timeData ? Object.keys(canData.timeData)
      .map(t => parseInt(t))
      .filter(t => t <= currentTimeMicros)
      .sort((a, b) => a - b) : [];
    
    const filtered = {
      ...canData,
      timeData: canData.timeData ? relevantTimes.reduce((acc, time) => {
        acc[time] = canData.timeData[time];
        return acc;
      }, {}) : {}
    };
    
    setFilteredData(filtered);
  }, [currentVideoTime, canData]);

  // Load CAN IDs and signals when project changes (not DBC file)
  useEffect(() => {
    if (!unitId || !selectedProject) return;

    setLoadingDbcInfo(true);
    const url = selectedDbcFile 
      ? `${process.env.REACT_APP_BASE_URL}/sessions/dbc-info/${unitId}/${selectedProject}?dbcFile=${encodeURIComponent(selectedDbcFile)}`
      : `${process.env.REACT_APP_BASE_URL}/sessions/dbc-info/${unitId}/${selectedProject}`;
    
    fetch(url, { credentials: 'include' })
    .then(response => response.json())
    .then(data => {
      if (data.canIds && data.signalsByCanId) {
        setCanIds(data.canIds);
        setSignalsByCanId(data.signalsByCanId);
        if (data.dbcFiles) setDbcFiles(data.dbcFiles);
        if (data.selectedDbcFile && !selectedDbcFile) setSelectedDbcFile(data.selectedDbcFile);
      }
    })
    .catch(error => console.error('Error loading DBC info:', error))
    .finally(() => setLoadingDbcInfo(false));
  }, [unitId, selectedProject, selectedDbcFile]);
  
  // Refetch chunks when DBC file changes
  const prevDbcFileRef = useRef(selectedDbcFile);
  useEffect(() => {
    if (selectedDbcFile && selectedSession && loadedChunksRef.current && prevDbcFileRef.current && prevDbcFileRef.current !== selectedDbcFile) {
      loadedChunksRef.current.set(selectedSession, new Set());
      fetchLogChunk(currentVideoTimeRef.current, selectedDbcFile);
    }
    prevDbcFileRef.current = selectedDbcFile;
  }, [selectedDbcFile, selectedSession, loadedChunksRef, fetchLogChunk]);

  // Download handlers
  const handleDownloadSession = (sessionFolder, e) => {
    e.stopPropagation();
    const downloadUrl = `${process.env.REACT_APP_BASE_URL}/sessions/download/${unitId}/${selectedProject}/${sessionFolder}`;
    window.open(downloadUrl, '_blank');
  };

  const handleDownloadProject = (e) => {
    e.stopPropagation();
    const downloadUrl = `${process.env.REACT_APP_BASE_URL}/sessions/download-project/${unitId}/${selectedProject}`;
    window.open(downloadUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div
      className="movable-frame unified-frame"
      style={{
        left: position.x,
        top: position.y,
        cursor: isDragging ? 'grabbing' : 'grab',
        width: '90vw',
        height: '90vh'
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="frame-header">
        <span>Session preview for {unitName || `Unit ${unitId}`}</span>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>
      <div className="frame-content">
        <SessionsList
          unitId={unitId}
          unitName={unitName}
          projects={projects}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          sessions={sessions}
          isLoadingSessions={isLoadingSessions}
          selectedSession={selectedSession}
          setSelectedSession={setSelectedSession}
          onDownloadProject={handleDownloadProject}
          onDownloadSession={handleDownloadSession}
        />

        <div className="right-panel">
          {selectedSession ? (
            <div className="session-preview">
              <PlayheadController
                isPlaying={isPlaying}
                currentVideoTime={currentVideoTime}
                sessionDuration={sessionDuration}
                onPlayPause={handlePlayPause}
                onPlayheadChange={handlePlayheadChangeWithFetch}
              />
              
              <div className="session-content">
                <SessionSummary
                  logData={logData}
                  projectInfo={projectInfo}
                  selectedSession={selectedSession}
                  sessionInfo={sessionInfo}
                  visibleFiles={visibleFiles}
                  setVisibleFiles={setVisibleFiles}
                  audioRef={audioRef}
                  isPlaying={isPlaying}
                  currentVideoTime={currentVideoTime}
                  videoRefs={videoRefs}
                  recreateRoute={recreateRoute}
                  unitId={unitId}
                  selectedProject={selectedProject}
                />
                
                {loading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px', flexDirection: 'column', gap: '16px' }}>
                    <div style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
                    <div style={{ fontSize: '14px', color: '#666' }}>Loading session data...</div>
                  </div>
                ) : (
                  <>
                    <MediaPlayer
                      videoFiles={videoFiles}
                      visibleFiles={visibleFiles}
                      videoRefs={videoRefs}
                      audioRef={audioRef}
                      unitId={unitId}
                      selectedProject={selectedProject}
                      selectedSession={selectedSession}
                      currentVideoTime={currentVideoTime}
                      isPlaying={isPlaying}
                      projectInfo={projectInfo}
                      currentGpsMessage={currentGpsMessage}
                      unitName={unitName}
                      mapRef={mapRef}
                      mapContainerRef={mapContainerRef}
                    />
                    
                    {canData || logData ? (
                  <div className="can-data-display">
                    <LogViewer
                      logData={logData}
                      dbLogData={dbLogData}
                      canIds={canIds}
                      currentVideoTime={currentVideoTime}
                      loadingChunks={loadingChunks}
                      dbcFiles={dbcFiles}
                      selectedDbcFile={selectedDbcFile}
                      onDbcFileChange={setSelectedDbcFile}
                    />
                  </div>
                    ) : (
                      <div className="no-data-message">No session data available</div>
                    )}
                    
                    <SignalPlotter
                      unitId={unitId}
                      selectedProject={selectedProject}
                      selectedSession={selectedSession}
                      currentVideoTime={currentVideoTime}
                      loadingDbcInfo={loadingDbcInfo}
                      canIds={canIds}
                      signalsByCanId={signalsByCanId}
                      sessionDuration={sessionDuration}
                      selectedDbcFile={selectedDbcFile}
                    />
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="select-session-message">
              <div>
                <h4>Select a session to view details</h4>
                <p>Choose a session from the list on the left to see the video and synchronized data.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}